/**
 * @fileoverview HTML sanitization utility to prevent XSS attacks.
 *
 * This module provides a secure way to render HTML content by sanitizing
 * it before rendering with dangerouslySetInnerHTML.
 *
 * Uses DOMPurify library for robust XSS prevention.
 */

import DOMPurify from "dompurify";

/**
 * Default DOMPurify configuration for safe HTML rendering.
 * Allows common HTML elements while blocking potentially dangerous ones.
 */
const DEFAULT_CONFIG = {
  // Allowed HTML tags
  ALLOWED_TAGS: [
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "br", "hr",
    "ul", "ol", "li",
    "table", "thead", "tbody", "tr", "th", "td",
    "div", "span",
    "a",
    "strong", "b", "em", "i", "u", "s", "strike",
    "code", "pre", "blockquote",
    "img",
    "sub", "sup",
  ],
  // Allowed HTML attributes
  ALLOWED_ATTR: [
    "href", "target", "rel",
    "src", "alt", "title", "width", "height",
    "class", "id",
    "colspan", "rowspan",
  ],
  // Force all links to open in new tab with security attributes
  ADD_ATTR: ["target", "rel"],
  // Forbid JavaScript URLs
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
  // Forbid dangerous tags
  FORBID_TAGS: ["script", "style", "iframe", "form", "input", "textarea", "button", "object", "embed"],
  // Allow data URIs for images only
  ALLOW_DATA_ATTR: false,
};

/**
 * Sanitizes HTML content to prevent XSS attacks.
 *
 * @param {string} html - The HTML string to sanitize
 * @param {Object} customConfig - Optional custom DOMPurify configuration
 * @returns {string} Sanitized HTML string safe for rendering
 *
 * @example
 * // Basic usage
 * const safeHtml = sanitizeHtml('<script>alert("xss")</script><p>Safe content</p>');
 * // Returns: '<p>Safe content</p>'
 *
 * @example
 * // With dangerouslySetInnerHTML
 * <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(untrustedHtml) }} />
 */
export const sanitizeHtml = (html, customConfig = {}) => {
  if (!html || typeof html !== "string") {
    return "";
  }

  const config = { ...DEFAULT_CONFIG, ...customConfig };
  return DOMPurify.sanitize(html, config);
};

/**
 * Creates a sanitized HTML object for use with dangerouslySetInnerHTML.
 *
 * @param {string} html - The HTML string to sanitize
 * @param {Object} customConfig - Optional custom DOMPurify configuration
 * @returns {Object} Object with __html property for dangerouslySetInnerHTML
 *
 * @example
 * <div dangerouslySetInnerHTML={createSafeHtml(untrustedHtml)} />
 */
export const createSafeHtml = (html, customConfig = {}) => {
  return { __html: sanitizeHtml(html, customConfig) };
};

/**
 * Hook for DOMPurify events to add security attributes to links.
 * This ensures all links open in new tabs and have proper security attributes.
 */
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  // Set all anchors to open in new tab with security attributes
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
  // Remove any javascript: URLs (intentional check - not a security issue)
  if (node.hasAttribute("href")) {
    const href = node.getAttribute("href");
    // eslint-disable-next-line no-script-url
    if (href.toLowerCase().startsWith("javascript:")) {
      node.removeAttribute("href");
    }
  }
});

export default sanitizeHtml;
